import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Building2, Store, MapPin, Euro, Star, Filter, ArrowUpRight, Calendar, Hammer, AlertTriangle } from 'lucide-react';
import { KPICard } from '../components/ui/KPICard';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis, ComposedChart, Line, Bar, Cell, BarChart } from 'recharts';
// Level options
const levels = [{
  id: 'konzern',
  name: 'Konzern-Übersicht',
  icon: Building2
}, {
  id: 'markt',
  name: 'Autohäuser',
  icon: Store
}];
const countries = [{
  id: 'deutschland',
  name: 'Deutschland'
}, {
  id: 'usa',
  name: 'USA'
}, {
  id: 'china',
  name: 'China'
}, {
  id: 'uk',
  name: 'Großbritannien'
}, {
  id: 'frankreich',
  name: 'Frankreich'
}, {
  id: 'australien',
  name: 'Australien'
}, {
  id: 'japan',
  name: 'Japan'
}];
// Autohaus data with investment metrics
const marketsData = [{
  id: 'hamburg-niederlassung',
  name: 'BMW Niederlassung Hamburg',
  region: 'Deutschland',
  rating: 4.3,
  ratingBefore: 3.9,
  investment: 850,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 2800
}, {
  id: 'berlin-welzel',
  name: 'Autohaus Welzel Berlin-Kreuzberg',
  region: 'Deutschland',
  rating: 3.8,
  ratingBefore: 3.6,
  investment: 120,
  lastRenovation: 2019,
  yearsSinceReno: 5,
  size: 1200
}, {
  id: 'muenchen-niederlassung',
  name: 'BMW Niederlassung München',
  region: 'Deutschland',
  rating: 4.6,
  ratingBefore: 4.1,
  investment: 620,
  lastRenovation: 2023,
  yearsSinceReno: 1,
  size: 1800
}, {
  id: 'nuernberg-niederlassung',
  name: 'BMW Niederlassung Nürnberg',
  region: 'Deutschland',
  rating: 4.3,
  ratingBefore: 3.9,
  investment: 380,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1700
}, {
  id: 'dresden-niederlassung',
  name: 'BMW Niederlassung Dresden',
  region: 'Deutschland',
  rating: 4.1,
  ratingBefore: 3.9,
  investment: 240,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1250
}, {
  id: 'stuttgart-niederlassung',
  name: 'BMW Niederlassung Stuttgart',
  region: 'Deutschland',
  rating: 4.4,
  ratingBefore: 4.0,
  investment: 410,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1650
}, {
  id: 'frankfurt-niederlassung',
  name: 'BMW Niederlassung Frankfurt',
  region: 'Deutschland',
  rating: 4.2,
  ratingBefore: 3.8,
  investment: 290,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1500
}, {
  id: 'manhattan-bmw',
  name: 'BMW of Manhattan, New York',
  region: 'USA',
  rating: 3.5,
  ratingBefore: 3.5,
  investment: 80,
  lastRenovation: 2017,
  yearsSinceReno: 7,
  size: 1100
}, {
  id: 'beverly-hills-bmw',
  name: 'BMW of Beverly Hills',
  region: 'USA',
  rating: 4.4,
  ratingBefore: 4.0,
  investment: 480,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1600
}, {
  id: 'chicago-bmw',
  name: 'BMW of Chicago',
  region: 'USA',
  rating: 3.7,
  ratingBefore: 3.6,
  investment: 150,
  lastRenovation: 2019,
  yearsSinceReno: 5,
  size: 1050
}, {
  id: 'dallas-bmw',
  name: 'BMW of Dallas',
  region: 'USA',
  rating: 4.1,
  ratingBefore: 3.8,
  investment: 320,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1400
}, {
  id: 'miami-bmw',
  name: 'BMW of Miami',
  region: 'USA',
  rating: 4.3,
  ratingBefore: 3.9,
  investment: 390,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1500
}, {
  id: 'beijing-bmw',
  name: 'BMW Center Beijing',
  region: 'China',
  rating: 3.9,
  ratingBefore: 3.7,
  investment: 220,
  lastRenovation: 2020,
  yearsSinceReno: 4,
  size: 1400
}, {
  id: 'shenyang-bmw',
  name: 'BMW Brilliance Shenyang',
  region: 'China',
  rating: 4.5,
  ratingBefore: 4.0,
  investment: 720,
  lastRenovation: 2023,
  yearsSinceReno: 1,
  size: 2200
}, {
  id: 'shanghai-bmw',
  name: "BMW Shanghai Jing'an",
  region: 'China',
  rating: 3.6,
  ratingBefore: 3.5,
  investment: 110,
  lastRenovation: 2018,
  yearsSinceReno: 6,
  size: 1300
}, {
  id: 'guangzhou-bmw',
  name: 'BMW Guangzhou Tianhe',
  region: 'China',
  rating: 4.0,
  ratingBefore: 3.7,
  investment: 280,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1350
}, {
  id: 'chengdu-bmw',
  name: 'BMW Chengdu Jinniu',
  region: 'China',
  rating: 4.2,
  ratingBefore: 3.8,
  investment: 350,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1550
}, {
  id: 'london-park-lane',
  name: 'BMW Park Lane, London',
  region: 'Großbritannien',
  rating: 4.2,
  ratingBefore: 3.9,
  investment: 350,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1500
}, {
  id: 'manchester-bmw',
  name: 'BMW Manchester Deansgate',
  region: 'Großbritannien',
  rating: 3.8,
  ratingBefore: 3.6,
  investment: 140,
  lastRenovation: 2019,
  yearsSinceReno: 5,
  size: 1100
}, {
  id: 'birmingham-bmw',
  name: 'BMW Birmingham Broad Street',
  region: 'Großbritannien',
  rating: 4.0,
  ratingBefore: 3.7,
  investment: 210,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1200
}, {
  id: 'edinburgh-bmw',
  name: 'BMW Edinburgh',
  region: 'Großbritannien',
  rating: 4.3,
  ratingBefore: 3.9,
  investment: 260,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 950
}, {
  id: 'paris-champs',
  name: 'BMW Paris Champs-Élysées',
  region: 'Frankreich',
  rating: 4.1,
  ratingBefore: 3.8,
  investment: 280,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1350
}, {
  id: 'lyon-bmw',
  name: 'BMW Lyon Part-Dieu',
  region: 'Frankreich',
  rating: 3.9,
  ratingBefore: 3.7,
  investment: 160,
  lastRenovation: 2020,
  yearsSinceReno: 4,
  size: 1000
}, {
  id: 'marseille-bmw',
  name: 'BMW Marseille Prado',
  region: 'Frankreich',
  rating: 3.7,
  ratingBefore: 3.6,
  investment: 90,
  lastRenovation: 2018,
  yearsSinceReno: 6,
  size: 950
}, {
  id: 'bordeaux-bmw',
  name: 'BMW Bordeaux Mériadeck',
  region: 'Frankreich',
  rating: 4.2,
  ratingBefore: 3.8,
  investment: 230,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1000
}, {
  id: 'sydney-bmw',
  name: 'BMW Sydney Rushcutters Bay',
  region: 'Australien',
  rating: 4.4,
  ratingBefore: 4.0,
  investment: 420,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1650
}, {
  id: 'melbourne-bmw',
  name: 'BMW Melbourne Collins Street',
  region: 'Australien',
  rating: 4.2,
  ratingBefore: 3.8,
  investment: 320,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 1550
}, {
  id: 'brisbane-bmw',
  name: 'BMW Brisbane Fortitude Valley',
  region: 'Australien',
  rating: 4.0,
  ratingBefore: 3.7,
  investment: 200,
  lastRenovation: 2020,
  yearsSinceReno: 4,
  size: 1200
}, {
  id: 'perth-bmw',
  name: 'BMW Perth Osborne Park',
  region: 'Australien',
  rating: 3.8,
  ratingBefore: 3.7,
  investment: 130,
  lastRenovation: 2019,
  yearsSinceReno: 5,
  size: 1050
}, {
  id: 'tokyo-bmw',
  name: 'BMW Tokyo Aoyama',
  region: 'Japan',
  rating: 4.0,
  ratingBefore: 3.8,
  investment: 180,
  lastRenovation: 2020,
  yearsSinceReno: 4,
  size: 1300
}, {
  id: 'osaka-bmw',
  name: 'BMW Osaka Midosuji',
  region: 'Japan',
  rating: 3.9,
  ratingBefore: 3.8,
  investment: 150,
  lastRenovation: 2018,
  yearsSinceReno: 6,
  size: 1150
}, {
  id: 'nagoya-bmw',
  name: 'BMW Nagoya Sakae',
  region: 'Japan',
  rating: 4.1,
  ratingBefore: 3.8,
  investment: 210,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1200
}, {
  id: 'fukuoka-bmw',
  name: 'BMW Fukuoka Tenjin',
  region: 'Japan',
  rating: 3.7,
  ratingBefore: 3.6,
  investment: 120,
  lastRenovation: 2019,
  yearsSinceReno: 5,
  size: 1000
}];
// Land aggregated data
const regionsData = [{
  id: 'deutschland',
  name: 'Deutschland',
  rating: 4.2,
  ratingBefore: 3.9,
  investment: 2210,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 9750,
  markets: 5
}, {
  id: 'usa',
  name: 'USA',
  rating: 3.95,
  ratingBefore: 3.75,
  investment: 560,
  lastRenovation: 2020,
  yearsSinceReno: 4,
  size: 2700,
  markets: 2
}, {
  id: 'china',
  name: 'China',
  rating: 4.2,
  ratingBefore: 3.85,
  investment: 940,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 3600,
  markets: 2
}, {
  id: 'uk',
  name: 'Großbritannien',
  rating: 4.2,
  ratingBefore: 3.9,
  investment: 350,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1500,
  markets: 1
}, {
  id: 'frankreich',
  name: 'Frankreich',
  rating: 4.1,
  ratingBefore: 3.8,
  investment: 280,
  lastRenovation: 2021,
  yearsSinceReno: 3,
  size: 1350,
  markets: 1
}, {
  id: 'australien',
  name: 'Australien',
  rating: 4.3,
  ratingBefore: 3.9,
  investment: 740,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 3200,
  markets: 2
}, {
  id: 'japan',
  name: 'Japan',
  rating: 3.95,
  ratingBefore: 3.8,
  investment: 330,
  lastRenovation: 2019,
  yearsSinceReno: 5,
  size: 2450,
  markets: 2
}];
// Konzern data
const konzernData = {
  id: 'konzern',
  name: 'BMW Gesamtnetz Weltweit',
  rating: 4.15,
  ratingBefore: 3.87,
  investment: 5410,
  lastRenovation: 2022,
  yearsSinceReno: 2,
  size: 24550,
  markets: 15,
  regions: 7
};
// Time series - Showroom-Investitionen und Rating über Jahre
const timeSeriesData = [{
  year: '2018',
  investment: 320,
  avgRating: 3.75
}, {
  year: '2019',
  investment: 450,
  avgRating: 3.82
}, {
  year: '2020',
  investment: 580,
  avgRating: 3.88
}, {
  year: '2021',
  investment: 870,
  avgRating: 3.98
}, {
  year: '2022',
  investment: 1850,
  avgRating: 4.08
}, {
  year: '2023',
  investment: 1340,
  avgRating: 4.15
}];
// Calculate correlation
const calculateCorrelation = (data: Array<{
  investment: number;
  rating: number;
}>) => {
  const n = data.length;
  if (n === 0) return 0;
  const sumX = data.reduce((acc, d) => acc + d.investment, 0);
  const sumY = data.reduce((acc, d) => acc + d.rating, 0);
  const sumXY = data.reduce((acc, d) => acc + d.investment * d.rating, 0);
  const sumX2 = data.reduce((acc, d) => acc + d.investment * d.investment, 0);
  const sumY2 = data.reduce((acc, d) => acc + d.rating * d.rating, 0);
  const numerator = n * sumXY - sumX * sumY;
  const denominator = Math.sqrt((n * sumX2 - sumX * sumX) * (n * sumY2 - sumY * sumY));
  return denominator === 0 ? 0 : numerator / denominator;
};
// Custom tooltip
const CustomScatterTooltip = ({
  active,
  payload
}: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const ratingImprovement = data.rating - data.ratingBefore;
    return <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm" data-id="element-628">
        <div className="font-semibold text-gray-900 mb-2" data-id="element-629">{data.name}</div>
        <div className="space-y-1" data-id="element-630">
          <div className="flex justify-between gap-4" data-id="element-631">
            <span className="text-gray-500" data-id="element-632">Aktuelles Rating:</span>
            <span className="font-medium" data-id="element-633">{data.rating.toFixed(1)} ★</span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-634">
            <span className="text-gray-500" data-id="element-635">Rating vorher:</span>
            <span className="font-medium" data-id="element-636">
              {data.ratingBefore.toFixed(1)} ★
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-637">
            <span className="text-gray-500" data-id="element-638">Verbesserung:</span>
            <span className="font-medium" style={{
            color: ratingImprovement > 0 ? '#639754' : '#E03C32'
          }} data-id="element-639">
              {ratingImprovement > 0 ? '+' : ''}
              {ratingImprovement.toFixed(2)} ★
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-640">
            <span className="text-gray-500" data-id="element-641">Investition:</span>
            <span className="font-medium" data-id="element-642">
              {data.investment.toLocaleString('de-DE')} T€
            </span>
          </div>
          <div className="flex justify-between gap-4" data-id="element-643">
            <span className="text-gray-500" data-id="element-644">Letzte Renovierung:</span>
            <span className="font-medium" data-id="element-645">{data.lastRenovation}</span>
          </div>
        </div>
      </div>;
  }
  return null;
};
export function InvestitionReputationPage() {
  const [selectedLevel, setSelectedLevel] = useState<string>('konzern');
  const getScatterData = () => {
    if (selectedLevel === 'konzern') return marketsData.map(m => ({
      ...m,
      z: m.size
    }));
    if (selectedLevel === 'markt') return marketsData.map(m => ({
      ...m,
      z: m.size
    }));
    const countryName = countries.find(c => c.id === selectedLevel)?.name;
    return marketsData.filter(m => m.region === countryName).map(m => ({
      ...m,
      z: m.size
    }));
  };
  const scatterData = getScatterData();
  const correlation = calculateCorrelation(scatterData);
  // Calculate averages
  const avgInvestment = scatterData.reduce((acc, d) => acc + d.investment, 0) / scatterData.length;
  const avgRating = scatterData.reduce((acc, d) => acc + d.rating, 0) / scatterData.length;
  const avgImprovement = scatterData.reduce((acc, d) => acc + (d.rating - d.ratingBefore), 0) / scatterData.length;
  const totalInvestment = scatterData.reduce((acc, d) => acc + d.investment, 0);
  // Find markets needing renovation (old renovation, low rating)
  const needsRenovation = [...scatterData].filter(d => d.yearsSinceReno >= 4 && d.rating < avgRating).sort((a, b) => b.yearsSinceReno - a.yearsSinceReno).slice(0, 3);
  // Find best ROI (high improvement, reasonable investment)
  const bestROI = [...scatterData].map(d => ({
    ...d,
    roi: (d.rating - d.ratingBefore) / (d.investment / 100)
  })).sort((a, b) => b.roi - a.roi).slice(0, 3);
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-646">
      {/* Level Filter */}
      <Card className="!p-4" data-id="element-647">
        <div className="flex items-center gap-4 flex-wrap" data-id="element-648">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-649">
            <Filter size={16} data-id="element-650" />
            <span data-id="element-651">Ebene:</span>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-652">
            {/* Konzern */}
            <button onClick={() => setSelectedLevel('konzern')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${selectedLevel === 'konzern' ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedLevel === 'konzern' ? {
            backgroundColor: '#1C69D4'
          } : {}} data-id="element-653">
              <Building2 size={14} data-id="element-654" />
              Konzern-Übersicht
            </button>

            {/* Divider */}
            <div className="flex items-center" data-id="element-655">
              <div className="w-px h-6 bg-gray-200 mx-1" data-id="element-656" />
            </div>

            {/* Country buttons */}
            {countries.map(country => <button key={country.id} onClick={() => setSelectedLevel(country.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedLevel === country.id ? 'text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} style={selectedLevel === country.id ? {
            backgroundColor: '#1C69D4'
          } : {}} data-id="element-657">
                {country.name}
              </button>)}
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-658">
        <KPICard title="Korrelation Invest ↔ Rating" value={correlation.toFixed(2)} change={correlation > 0.6 ? 'Stark positiv' : correlation > 0.3 ? 'Moderat' : 'Schwach'} trend="up" icon={<TrendingUp size={18} data-id="element-660" />} subtext="Pearson-Koeffizient" data-id="element-659" />
        <KPICard title="Gesamtinvestition" value={`${(totalInvestment / 1000).toFixed(1)}M €`} change="letzte 5 Jahre" trend="up" icon={<Euro size={18} data-id="element-662" />} subtext="Showroom-Modernisierungen" data-id="element-661" />
        <KPICard title="Ø Rating-Verbesserung" value={`+${avgImprovement.toFixed(2)}★`} change="nach Modernisierung" trend="up" icon={<Star size={18} data-id="element-664" />} subtext="pro Autohaus" data-id="element-663" />
        <KPICard title="Modernisierungsbedarf" value={needsRenovation.length.toString()} change=">4 Jahre alt" trend="down" icon={<Hammer size={18} data-id="element-666" />} subtext="Autohäuser" data-id="element-665" />
      </div>

      {/* Main Scatter Chart */}
      <Card data-id="element-667">
        <div className="p-6" data-id="element-668">
          <div className="flex items-center justify-between mb-6" data-id="element-669">
            <div data-id="element-670">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-671">
                Showroom-Investition vs. Kundenbewertung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-672">
                Blasengröße = Showroomfläche (m²) • Investition in Tsd. €
              </p>
            </div>
            <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-673">
              <div className="flex items-center gap-1" data-id="element-674">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#639754'
              }} data-id="element-675"></div>
                <span data-id="element-676">Kürzlich modernisiert (≤2J)</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-677">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-678"></div>
                <span data-id="element-679">Modernisierung nötig (≥5J)</span>
              </div>
            </div>
          </div>
          <div className="h-96" data-id="element-680">
            <ResponsiveContainer width="100%" height="100%" data-id="element-681">
              <ScatterChart margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }} data-id="element-682">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-683" />
                <XAxis type="number" dataKey="investment" name="Investition" domain={[0, 1000]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={v => `${v}T€`} label={{
                value: 'Investition (Tsd. €)',
                position: 'bottom',
                offset: 0,
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-684" />
                <YAxis type="number" dataKey="rating" name="Rating" domain={[3.2, 5]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} label={{
                value: 'Google Rating (Sterne)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#6b7280'
                }
              }} data-id="element-685" />
                <ZAxis type="number" dataKey="z" range={[100, 800]} data-id="element-686" />
                <Tooltip content={<CustomScatterTooltip data-id="element-688" />} data-id="element-687" />
                <Scatter name="Autohäuser" data={scatterData} fill="#1C69D4" data-id="element-689">
                  {scatterData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.yearsSinceReno <= 2 ? '#639754' : entry.yearsSinceReno <= 4 ? '#F59E0B' : '#E03C32'} fillOpacity={0.7} data-id="element-690" />)}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm" data-id="element-691">
            <div className="flex items-center gap-2" data-id="element-692">
              <TrendingUp size={16} style={{
              color: '#639754'
            }} data-id="element-693" />
              <span className="text-gray-600" data-id="element-694">
                Korrelation:{' '}
                <span className="font-semibold" style={{
                color: correlation > 0.5 ? '#639754' : '#F59E0B'
              }} data-id="element-695">
                  {(correlation * 100).toFixed(0)}%
                </span>{' '}
                – Höhere Investitionen korrelieren mit besseren Bewertungen
              </span>
            </div>
            <div className="text-gray-500" data-id="element-696">
              Ø Investition: {avgInvestment.toFixed(0)} T€
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-697">
        {/* Best ROI */}
        <Card data-id="element-698">
          <div className="p-6" data-id="element-699">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-700">
              <ArrowUpRight className="text-green-600" size={20} data-id="element-701" />
              Beste Investitionsrendite (ROI)
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-702">
              Höchste Rating-Verbesserung pro investiertem Euro
            </p>
            <div className="space-y-3" data-id="element-703">
              {bestROI.map((item, index) => {
              const improvement = item.rating - item.ratingBefore;
              return <motion.div key={item.id} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: index * 0.1
              }} className="flex items-center justify-between p-3 rounded-lg" style={{
                backgroundColor: '#e8f0e5'
              }} data-id="element-704">
                    <div className="flex items-center gap-3" data-id="element-705">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                    backgroundColor: '#639754'
                  }} data-id="element-706">
                        {index + 1}
                      </div>
                      <div data-id="element-707">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-708">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-709">
                          {item.investment.toLocaleString('de-DE')} T€ •
                          Modernisiert {item.lastRenovation}
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-710">
                      <div className="font-bold text-lg" style={{
                    color: '#639754'
                  }} data-id="element-711">
                        +{improvement.toFixed(2)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-712">
                        {item.ratingBefore.toFixed(1)} →{' '}
                        {item.rating.toFixed(1)}
                      </div>
                    </div>
                  </motion.div>;
            })}
            </div>
          </div>
        </Card>

        {/* Needs Renovation */}
        <Card data-id="element-713">
          <div className="p-6" data-id="element-714">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-715">
              <AlertTriangle className="text-red-600" size={20} data-id="element-716" />
              Modernisierungsbedarf
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-717">
              Autohäuser mit veralteter Ausstattung und unterdurchschnittlichem
              Rating
            </p>
            <div className="space-y-3" data-id="element-718">
              {needsRenovation.length > 0 ? needsRenovation.map((item, index) => <motion.div key={item.id} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: index * 0.1
            }} className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-719">
                    <div className="flex items-center gap-3" data-id="element-720">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-721">
                        !
                      </div>
                      <div data-id="element-722">
                        <div className="font-medium text-gray-900 text-sm" data-id="element-723">
                          {item.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-724">
                          Letzte Modernisierung: {item.lastRenovation} (
                          {item.yearsSinceReno} Jahre)
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-725">
                      <div className="font-bold text-lg" style={{
                  color: '#E03C32'
                }} data-id="element-726">
                        {item.rating.toFixed(1)} ★
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-727">
                        {((avgRating - item.rating) / avgRating * 100).toFixed(0)}
                        % unter Ø
                      </div>
                    </div>
                  </motion.div>) : <div className="text-center py-8 text-gray-500" data-id="element-728">
                  Kein dringender Modernisierungsbedarf
                </div>}
            </div>
            {needsRenovation.length > 0 && <div className="mt-4 p-3 bg-blue-50 rounded-lg" data-id="element-729">
                <div className="text-sm text-blue-800" data-id="element-730">
                  <strong data-id="element-731">Empfehlung:</strong> Basierend auf historischen Daten
                  kann eine Showroom-Modernisierung eine Rating-Verbesserung von
                  ca. <strong data-id="element-732">+{avgImprovement.toFixed(2)} Sternen</strong>{' '}
                  bewirken.
                </div>
              </div>}
          </div>
        </Card>
      </div>

      {/* Time Series Chart */}
      <Card data-id="element-733">
        <div className="p-6" data-id="element-734">
          <div className="flex items-center justify-between mb-6" data-id="element-735">
            <div data-id="element-736">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-737">
                Showroom-Investitionen und Rating-Entwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-738">
                Jährliche Investitionen und durchschnittliche Bewertung
              </p>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-739">
              <div className="flex items-center gap-2" data-id="element-740">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#1C69D4'
              }} data-id="element-741"></div>
                <span className="text-gray-600" data-id="element-742">Investition (Mio. €)</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-743">
                <div className="w-6 h-0.5" style={{
                backgroundColor: '#F59E0B'
              }} data-id="element-744"></div>
                <span className="text-gray-600" data-id="element-745">Ø Rating</span>
              </div>
            </div>
          </div>
          <div className="h-72" data-id="element-746">
            <ResponsiveContainer width="100%" height="100%" data-id="element-747">
              <ComposedChart data={timeSeriesData} data-id="element-748">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-749" />
                <XAxis dataKey="year" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-750" />
                <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[0, 2000]} tickFormatter={v => `${(v / 1000).toFixed(1)}M`} label={{
                value: 'Investition (Mio. €)',
                angle: -90,
                position: 'insideLeft',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-751" />
                <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} domain={[3.5, 4.5]} label={{
                value: 'Rating',
                angle: 90,
                position: 'insideRight',
                style: {
                  fontSize: 11,
                  fill: '#9ca3af'
                }
              }} data-id="element-752" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => [name === 'investment' ? `${(value / 1000).toFixed(2)} Mio. €` : value.toFixed(2), name === 'investment' ? 'Investition' : 'Ø Rating']} data-id="element-753" />
                <Bar yAxisId="left" dataKey="investment" fill="#1C69D4" radius={[4, 4, 0, 0]} barSize={40} data-id="element-754" />
                <Line yAxisId="right" type="monotone" dataKey="avgRating" stroke="#F59E0B" strokeWidth={3} dot={{
                fill: '#F59E0B',
                strokeWidth: 2,
                r: 5
              }} data-id="element-755" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-center" data-id="element-756">
            <div data-id="element-757">
              <div className="text-xs text-gray-500 mb-1" data-id="element-758">
                Gesamtinvestition (6 Jahre)
              </div>
              <div className="text-lg font-bold" style={{
              color: '#1C69D4'
            }} data-id="element-759">
                {(timeSeriesData.reduce((acc, d) => acc + d.investment, 0) / 1000).toFixed(1)}{' '}
                Mio. €
              </div>
            </div>
            <div data-id="element-760">
              <div className="text-xs text-gray-500 mb-1" data-id="element-761">
                Rating-Verbesserung (6 Jahre)
              </div>
              <div className="text-lg font-bold" style={{
              color: '#639754'
            }} data-id="element-762">
                +
                {(timeSeriesData[timeSeriesData.length - 1].avgRating - timeSeriesData[0].avgRating).toFixed(2)}
                ★
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Data Table */}
      <Card data-id="element-763">
        <div className="p-6" data-id="element-764">
          <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-765">
            Detailübersicht{' '}
            {selectedLevel === 'markt' ? 'Autohäuser' : selectedLevel === 'konzern' ? 'Konzern' : countries.find(c => c.id === selectedLevel)?.name || 'Länder'}
          </h3>
          <div className="overflow-x-auto" data-id="element-766">
            <table className="w-full text-sm" data-id="element-767">
              <thead data-id="element-768">
                <tr className="border-b border-gray-200" data-id="element-769">
                  <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-770">
                    Name
                  </th>
                  {selectedLevel === 'markt' && <th className="text-left py-3 px-4 font-medium text-gray-500" data-id="element-771">
                      Land
                    </th>}
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-772">
                    Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-773">
                    Vorher
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-774">
                    Δ Rating
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-775">
                    Investition
                  </th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500" data-id="element-776">
                    Modernisierung
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-777">
                {scatterData.map((item: any, index) => {
                const improvement = item.rating - item.ratingBefore;
                return <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-778">
                      <td className="py-3 px-4 font-medium text-gray-900" data-id="element-779">
                        {item.name}
                      </td>
                      {selectedLevel === 'markt' && <td className="py-3 px-4 text-gray-500" data-id="element-780">
                          {item.region}
                        </td>}
                      <td className="py-3 px-4 text-right" data-id="element-781">
                        <span className={`font-medium ${item.rating >= 4.3 ? 'text-green-600' : item.rating >= 3.8 ? 'text-gray-900' : 'text-red-600'}`} data-id="element-782">
                          {item.rating.toFixed(1)} ★
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500" data-id="element-783">
                        {item.ratingBefore.toFixed(1)} ★
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-784">
                        <span className={`font-medium ${improvement > 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-785">
                          {improvement > 0 ? '+' : ''}
                          {improvement.toFixed(2)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right text-gray-900" data-id="element-786">
                        {item.investment.toLocaleString('de-DE')} T€
                      </td>
                      <td className="py-3 px-4 text-right" data-id="element-787">
                        <Badge variant={item.yearsSinceReno <= 2 ? 'success' : item.yearsSinceReno <= 4 ? 'warning' : 'danger'} data-id="element-788">
                          {item.lastRenovation}
                        </Badge>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>;
}